import React, { useCallback, useEffect, useState } from "react";
import StatusPill from "../elements/StatusPill";

import "../../style/environment-container.module.css";
import { SassAppDevData, SassAppProductionData } from "../../dashboard/saas/types";
import Modal, { ModalProps } from "../../useOss/Modal";
import getLatestCoreVersion from "../../api/st/core/latest/get";
import { UpgradeCoreVersion } from "./UpgradeCoreVersion";
import { getTeleportAvailable } from "../../api/saas/multi-tenancy/deployment/teleport/available";

interface Props {
    title: string;
    status: "Active" | "Stopped" | "Restarting" | undefined;
    appDetails: SassAppDevData | SassAppProductionData | undefined;
    renderChildrenBeforeTitle?: () => React.ReactNode;
    hideStatus?: boolean;
    children: React.ReactNode;
    onClearData: (deploymentId: string) => void;
    isProductionInstance?: boolean;
    regionImage?: string;
    region: string;
}

type ConfirmationProps = Pick<Props, "onClearData" | "appDetails"> &
    ModalProps & {
        isProductionInstance?: boolean;
    };

const ClearEnvironmentDataConfirmation = (props: ConfirmationProps) => {
    const { appDetails, onClearData, show, modalClosed } = props;
    const isProduction = props.isProductionInstance === true;
    const [isDeletingData, setIsDeletingData] = useState(false);

    const clearData = useCallback(async () => {
        setIsDeletingData(true);
        try {
            if (appDetails !== undefined) {
                await onClearData(appDetails.id);
            }
        } finally {
            setIsDeletingData(true);
            modalClosed();
        }
    }, [onClearData, appDetails]);

    return (
        <React.Fragment>
            <Modal show={show} modalClosed={modalClosed}>
                <div className="saas-modal">
                    <div className="saas-modal__header">
                        <h2>Clear Data?</h2>
                        <span className="close-button" onClick={modalClosed}>
                            <img src="static/assets/dashboard/close-rounded.png" />
                        </span>
                    </div>
                    <div className="saas-modal__body">
                        <p>
                            Are you sure you want to delete all your data in{" "}
                            {isProduction ? "production" : "development"} environment?
                        </p>
                    </div>
                    <div className="saas-modal__footer">
                        <div className="saas-modal__action">
                            <button className="btn btn-outline-dark" onClick={modalClosed}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger btn-confirm btn-clear-data"
                                onClick={clearData}
                                disabled={isDeletingData}
                            >
                                {" "}
                                {isDeletingData ? "Deleting..." : "Yes, Delete"}{" "}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </React.Fragment>
    );
};

const EnvironmentContainer = (props: Props) => {
    const { appDetails, hideStatus, children, title, status, region } = props;
    const [latestCoreVersion, setLatestCoreVersion] = useState<string | undefined>(undefined);
    const [isTeleportAvailable, setIsTeleportAvailable] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showUpdateCoreVersion, setShowUpdateCoreVersion] = useState(false);

    const showVersion = appDetails !== undefined && appDetails.supertokensVersion !== undefined;
    const version =
        appDetails !== undefined
            ? appDetails.displayCoreVersion !== undefined
                ? appDetails.displayCoreVersion
                : appDetails.supertokensVersion
            : "";

    useEffect(() => {
        async function getCoreVersion() {
            try {
                const deploymentType = props.isProductionInstance === true ? "production" : "development";
                const response = await getLatestCoreVersion({ deploymentType, region });
                setLatestCoreVersion(response.coreVersion);
                if (response.coreVersion && version) {
                    const isUpdateAvailable = parseFloat(response.coreVersion) > parseFloat(version) ? true : false;
                    setShowUpdateCoreVersion(isUpdateAvailable);
                }
            } catch (_) {}
        }
        getCoreVersion();
    }, [version]);

    useEffect(() => {
        async function isTeleportAvailable() {
            try {
                const response = await getTeleportAvailable({
                    deploymentType: props.isProductionInstance === true ? "production" : "development"
                });
                setIsTeleportAvailable(response.available);
            } catch (_) {}
        }
        isTeleportAvailable();
    }, [version]);

    return (
        <div
            className="env-container"
            style={{
                marginTop: props.isProductionInstance === true ? "32px" : "0px"
            }}
        >
            {props.renderChildrenBeforeTitle !== undefined && props.renderChildrenBeforeTitle()}

            <div
                className={`env-container__title ${
                    props.renderChildrenBeforeTitle !== undefined ? "with-top-border" : ""
                }`}
            >
                <span>
                    {title} {showVersion ? `(v${version})` : ""}
                </span>
                {props.regionImage && <img alt="region-flag" src={props.regionImage} className="region-flag" />}

                {!hideStatus && <StatusPill status={status || "Stopped"} />}

                <div className="break"></div>

                {showUpdateCoreVersion && isTeleportAvailable ? (
                    <UpgradeCoreVersion
                        version={version}
                        isProductionInstance={props.isProductionInstance === true}
                        latestCoreVersion={latestCoreVersion}
                    />
                ) : null}

                {!hideStatus && props.isProductionInstance !== true && (
                    <button
                        className="btn btn-outline-danger btn-sm btn-clear-data"
                        onClick={() => setShowConfirmation(true)}
                    >
                        Clear Data
                    </button>
                )}
            </div>
            <div className="env-container__content">{children}</div>
            {showConfirmation && (
                <ClearEnvironmentDataConfirmation
                    {...props}
                    isProductionInstance={props.isProductionInstance}
                    show={showConfirmation}
                    modalClosed={() => setShowConfirmation(false)}
                />
            )}
        </div>
    );
};

export default EnvironmentContainer;
